import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { RichLine } from "./Reveal";

const EASE_EXIT = [0.77, 0, 0.175, 1];
const EASE = [0.22, 1, 0.36, 1];

export const Preloader = ({ onReady }) => {
  const { t } = useLang();
  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState("loading");
  const called = useRef(false);

  useEffect(() => {
    window.lenis?.stop();
    document.body.style.overflow = "hidden";
    const start = performance.now();
    const dur = 2100;
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setCount(Math.floor(p * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else {
        setPhase("exiting");
        if (!called.current) {
          called.current = true;
          setTimeout(onReady, 300);
        }
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onReady]);

  if (phase === "hidden") return null;

  return (
    <motion.div
      data-testid="preloader"
      className="fixed inset-0 z-[200] bg-[#faf9f6] flex flex-col items-center justify-center px-5"
      animate={phase === "exiting" ? { y: "-100%" } : { y: 0 }}
      transition={{ duration: 1.05, ease: EASE_EXIT }}
      onAnimationComplete={() => {
        if (phase === "exiting") {
          setPhase("hidden");
          document.body.style.overflow = "";
          window.lenis?.start();
        }
      }}
    >
      <motion.img
        src="/logos/emberline-final-512.png"
        alt="Emberline Chimney Co."
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.05, duration: 0.8, ease: EASE }}
        className="w-24 h-24 sm:w-28 sm:h-28 object-contain mb-6 drop-shadow-[0_0_24px_rgba(255,107,0,0.25)]"
      />
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.7 }}
        className="font-tech text-[10px] sm:text-xs tracking-[0.45em] uppercase text-neutral-400 mb-8"
      >
        Emberline Chimney Co.
      </motion.p>
      <div className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.12] tracking-tight text-neutral-900 text-center">
        {t.preloader.lines.map((line, i) => (
          <span key={i} className="block overflow-hidden py-0.5">
            <motion.span
              className="block"
              initial={{ y: "115%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.2 + i * 0.16, ease: EASE }}
            >
              <RichLine segments={line} />
            </motion.span>
          </span>
        ))}
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        data-testid="preloader-counter"
        className="absolute bottom-6 right-7 sm:bottom-8 sm:right-10 font-display text-6xl sm:text-8xl text-neutral-900 tabular-nums"
      >
        {count}<span className="text-gradient-fire">%</span>
      </motion.p>
    </motion.div>
  );
};
